"use client"

import { Smartphone } from "lucide-react"

import { accountText, type LocaleProps } from "../../personal-web"

type Props = LocaleProps & {
  /** Raw LPA string like "LPA:1$wbg.prod...$3JTP..." */
  lpa: string
  /** Optional click handler for analytics. Navigation still happens via href. */
  onTapped?: () => void
}

/**
 * Apple's universal eSIM installation link: tapping it on an iPhone running
 * iOS 17.4+ hands the activation off to the OS — no QR scan, no menu
 * spelunking. Older iOS versions fall back to opening a Safari page with
 * instructions, so it's safe to always render (the deeplink itself doesn't
 * fail loudly on unsupported devices).
 *
 * Caller should only render this when `supportsIosDeeplink(detected) === true`
 * — see useDeviceDetect.ts.
 */
export function TapToInstallButton({ lng, lpa, onTapped }: Props) {
  const href = lpa
    ? `https://esimsetup.apple.com/esim_qrcode_provisioning?carddata=${encodeURIComponent(lpa)}`
    : undefined

  if (!href) {
    return null
  }

  return (
    <a
      href={href}
      onClick={onTapped}
      className="group relative flex min-h-14 w-full items-center justify-center gap-3 overflow-hidden rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 px-6 py-4 text-base font-extrabold text-white shadow-[0_8px_24px_-12px_rgba(234,88,12,0.6)] transition-all duration-200 hover:from-orange-600 hover:to-amber-600 hover:shadow-[0_12px_28px_-10px_rgba(234,88,12,0.7)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500 active:scale-[0.99]"
    >
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 50%, white 0, transparent 40%)",
        }}
      />
      <Smartphone className="relative h-5 w-5" aria-hidden />
      <span className="relative">
        {accountText(lng, "install.deeplink.cta")}
      </span>
    </a>
  )
}
